// Board expansions (IMPLEMENTATION §0.3/§0.4, GDD §3.2; M4a). The world grid is fixed (7 × 9); an expansion
// unmasks a bigger centred rectangle that contains the previous one (data check), so no tile ever moves:
// only rock cells become floor. Masks are bought in order: 3×3 (start) → 5×5 ($1K, u10) → 5×7 ($12K, u18)
// → v0.2 7×7 / 7×9. `expand {id}` needs the `expansion.<id>` effect, the previous mask and the cash; it is
// paid from cash (not refundable, not a build-undo entry) and emits `expand`. The occupancy mask and the
// chute topology are refreshed at once (a chain that ended on rock may now reach floor).
import type { RejectReason } from './commands.ts';
import type { Int } from './core/types.ts';
import type { Ctx } from './ctx.ts';
import { type ExpansionDef, isActive, type SimDefs } from './defs.ts';
import type { EffectId, ExpansionId } from './gen/ids.ts';
import { etaPerSec } from './income.ts';
import { maskOf } from './occupancy.ts';
import { etaSeconds } from './rules/goal.ts';
import { expansionIndex } from './rules/triggers.ts';
import type { SimState } from './state.ts';
import { hasEffect } from './unlocks.ts';

/** Why `id` can't be bought now, or null. */
export function expandBlock(s: SimState, d: SimDefs, id: ExpansionId): RejectReason | null {
  const i = expansionIndex(d, id);
  const e = d.board.expansions[i];
  if (e === undefined) return 'stale';
  if (i <= s.bounds) return 'stale'; // already open
  if (!isActive(d, e.version) || !hasEffect(s, d, `expansion.${id}` as EffectId)) {
    return 'notUnlocked';
  }
  if (i !== s.bounds + 1) return 'locked'; // "Expand first": the smaller mask comes first
  if (s.wallet.cash < e.cost) return 'noCash';
  return null;
}

/** Buys expansion `id` (validated by expandBlock). */
export function applyExpand(c: Ctx, id: ExpansionId): void {
  const i = expansionIndex(c.d, id);
  const e = c.d.board.expansions[i] as ExpansionDef;
  c.s.wallet.cash -= e.cost;
  c.s.bounds = i;
  c.occ.mask = maskOf(c.s, c.d);
  c.topo = null;
  c.ev.push({ e: 'expand', id });
}

export type ExpansionState = 'open' | 'next' | 'later';

export interface ExpansionView {
  id: ExpansionId;
  cost: Int;
  /** Inclusive world rectangle. */
  x0: Int;
  y0: Int;
  x1: Int;
  y1: Int;
  state: ExpansionState;
  /** Why the Buy button is disabled (null = buyable now); null for open masks. */
  block: RejectReason | null;
  /**
   * Expansion card ETA (UX §3.5 "price, ETA, Buy") for the `next` mask: seconds until cash reaches the cost
   * at the total income over the ETA window (0 = now, null = no income); null for open / later masks.
   */
  etaSec: Int | null;
}

/** Every version-active mask with its state (Expansion card / Board shop category). */
export function expansionView(s: SimState, d: SimDefs): ExpansionView[] {
  const perSec = etaPerSec(s, d);
  const out: ExpansionView[] = [];
  d.board.expansions.forEach((e, i) => {
    if (!isActive(d, e.version)) return;
    const state: ExpansionState = i <= s.bounds ? 'open' : i === s.bounds + 1 ? 'next' : 'later';
    out.push({
      id: e.id,
      cost: e.cost,
      x0: e.x0,
      y0: e.y0,
      x1: e.x1,
      y1: e.y1,
      state,
      block: state === 'open' ? null : expandBlock(s, d, e.id),
      etaSec: state === 'next' ? etaSeconds(e.cost, s.wallet.cash, perSec) : null,
    });
  });
  return out;
}
